import { ACTOR_ROLES, type ActorRole } from '../common/enums.js';
import * as admin from '../schemas/admin.js';
import * as assist from '../schemas/ai-assist.js';
import * as attachments from '../schemas/attachments.js';
import * as auth from '../schemas/auth.js';
import * as catalog from '../schemas/catalog.js';
import * as deliverables from '../schemas/deliverables.js';
import * as graph from '../schemas/method-graph.js';
import * as methods from '../schemas/methods.js';
import * as planning from '../schemas/planning.js';
import * as publishing from '../schemas/publishing.js';
import * as validation from '../schemas/validation.js';
import * as workshop from '../schemas/workshop.js';
import { route, type AnyRoute } from './types.js';

/**
 * THE route manifest. Every HTTP endpoint of the platform is declared exactly
 * once here, and both the typed client and the OpenAPI document are derived from
 * it. Adding an endpoint anywhere else is a defect the exhaustiveness test in
 * `manifest.test.ts` is written to catch.
 */

/** Role bundles. An empty list means the route is public. */
const PUBLIC: readonly ActorRole[] = [];
const ANY_ROLE: readonly ActorRole[] = ACTOR_ROLES;
const ADMIN_ONLY: readonly ActorRole[] = ['ADMIN'];
const AUTHORS: readonly ActorRole[] = ['ADMIN', 'METHOD_AUTHOR'];
const REVIEWERS: readonly ActorRole[] = ['ADMIN', 'REVIEWER'];
const FACILITATORS: readonly ActorRole[] = ['ADMIN', 'WORKSHOP_FACILITATOR'];
/** Holders of `deliverable:manage`: whoever may cut a template version. */
const AUTHORS_AND_FACILITATORS: readonly ActorRole[] = [
  'ADMIN',
  'METHOD_AUTHOR',
  'WORKSHOP_FACILITATOR',
];
/**
 * Holders of `catalog:manageSession` — whoever may write into one adoption's own
 * catalogue.
 *
 * The same three roles as `AUTHORS_AND_FACILITATORS`, and a bundle of its own for the
 * same reason `PLANNERS` is: they are these three for a different reason. A facilitator
 * is here because they are in the room when the client names its tools; an author is
 * here because `catalog:manage` already lets them rewrite the platform catalogue, so the
 * narrower grant follows. If `deliverable:manage` were ever narrowed, the session
 * catalogue should not silently follow it.
 *
 * Note what the bundle cannot express: holding the capability is not the same as being
 * allowed into *this* session. `requireSessionCatalogWrite` adds that, because a route
 * manifest describes audiences and not ownership.
 */
const SESSION_CATALOGUERS: readonly ActorRole[] = ['ADMIN', 'METHOD_AUTHOR', 'WORKSHOP_FACILITATOR'];
/**
 * Holders of `plan:manage`. The same three as above and declared separately anyway,
 * because they are the same people for a different reason: a plan is generated by whoever
 * ran the adoption or owns the method, and if `deliverable:manage` were ever narrowed the
 * planning routes should not silently follow it.
 */
const PLANNERS: readonly ActorRole[] = ['ADMIN', 'METHOD_AUTHOR', 'WORKSHOP_FACILITATOR'];
/**
 * Holders of `attachment:upload` and `assist:invoke` — everyone who contributes to a
 * method, which includes the REVIEWER. A reviewer attaches the annotated screenshot of the
 * gate that is wrong instead of describing it, and asks the assistant what changed since
 * the last revision; neither is a write to a method, because assist output is applied
 * through an authoring command a reviewer cannot issue and an attachment is inert until
 * confirmed.
 */
const CONTRIBUTORS: readonly ActorRole[] = [
  'ADMIN',
  'METHOD_AUTHOR',
  'REVIEWER',
  'WORKSHOP_FACILITATOR',
];

export const ROUTES = {
  /* ── auth ─────────────────────────────────────────────────────────────── */
  /**
   * Public because it is what decides whether the sign-in screen renders a federated
   * button, and the sign-in screen is reached without a token. It returns configuration
   * *state* — enabled or not — never configuration values.
   *
   * The w3id start and callback endpoints are NOT here: both are browser redirects, and
   * this manifest describes typed JSON operations (docs/architecture/04-W3ID-SSO.md §8).
   * They are named in `NOT_API_OPERATIONS` in `apps/api/src/route-conformance.spec.ts`,
   * which is the stated exemption rather than a silent omission.
   */
  authProviders: route({
    operationId: 'authProviders',
    method: 'GET',
    path: '/api/auth/providers',
    context: 'auth',
    summary: 'Which federated sign-in providers this deployment offers',
    request: auth.AuthProvidersRequestSchema,
    response: auth.AuthProvidersResponseSchema,
    roles: PUBLIC,
    successStatus: 200,
  }),
  authRegister: route({
    operationId: 'authRegister',
    method: 'POST',
    path: '/api/auth/register',
    context: 'auth',
    summary: 'Register a local account',
    request: auth.RegisterRequestSchema,
    response: auth.RegisterResponseSchema,
    roles: PUBLIC,
    successStatus: 201,
    errors: ['ALREADY_EXISTS', 'FORBIDDEN'],
  }),
  authLogin: route({
    operationId: 'authLogin',
    method: 'POST',
    path: '/api/auth/login',
    context: 'auth',
    summary: 'Exchange credentials for an access token and refresh cookie',
    request: auth.LoginRequestSchema,
    response: auth.LoginResponseSchema,
    roles: PUBLIC,
    successStatus: 200,
    errors: ['UNAUTHENTICATED', 'RATE_LIMITED'],
  }),
  authRefresh: route({
    operationId: 'authRefresh',
    method: 'POST',
    path: '/api/auth/refresh',
    context: 'auth',
    summary: 'Rotate the refresh cookie and issue a new access token',
    request: auth.RefreshRequestSchema,
    response: auth.RefreshResponseSchema,
    roles: PUBLIC,
    successStatus: 200,
    errors: ['UNAUTHENTICATED'],
  }),
  authLogout: route({
    operationId: 'authLogout',
    method: 'POST',
    path: '/api/auth/logout',
    context: 'auth',
    summary: 'Revoke the current session, optionally every session',
    request: auth.LogoutRequestSchema,
    response: auth.LogoutResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  authMe: route({
    operationId: 'authMe',
    method: 'GET',
    path: '/api/auth/me',
    context: 'auth',
    summary: 'The authenticated user and effective roles',
    request: auth.MeRequestSchema,
    response: auth.MeResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),

  /* ── admin ────────────────────────────────────────────────────────────── */
  adminGetSettings: route({
    operationId: 'adminGetSettings',
    method: 'GET',
    path: '/api/admin/settings',
    context: 'admin',
    summary: 'Read admin settings and the masked Bedrock credential status',
    request: admin.GetAdminSettingsRequestSchema,
    response: admin.GetAdminSettingsResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
  }),
  adminUpdateSettings: route({
    operationId: 'adminUpdateSettings',
    method: 'PUT',
    path: '/api/admin/settings',
    context: 'admin',
    summary: 'Replace admin settings',
    request: admin.PutAdminSettingsRequestSchema,
    response: admin.PutAdminSettingsResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
  }),
  adminPutBedrockCredential: route({
    operationId: 'adminPutBedrockCredential',
    method: 'PUT',
    path: '/api/admin/settings/bedrock-credential',
    context: 'admin',
    summary: 'Store the Bedrock credential (write-only; response is masked)',
    request: admin.PutBedrockCredentialRequestSchema,
    response: admin.PutBedrockCredentialResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    errors: ['VALIDATION_FAILED'],
  }),
  adminTestBedrockConnection: route({
    operationId: 'adminTestBedrockConnection',
    method: 'POST',
    path: '/api/admin/settings/bedrock-credential/test',
    context: 'admin',
    summary: 'Invoke Bedrock with the stored credential and report the result',
    request: admin.TestConnectionRequestSchema,
    response: admin.TestConnectionResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    errors: ['ASSIST_UNAVAILABLE', 'DEPENDENCY_UNAVAILABLE'],
  }),

  /* ── admin: accounts and role assignments ─────────────────────────────────
     The five routes that make RBAC administrable. ADMIN_ONLY, and the only
     operations in the manifest whose responses carry another person's email
     address — see the note in `schemas/admin.ts` for why that exception is
     confined to here. */
  adminListAccounts: route({
    operationId: 'adminListAccounts',
    method: 'GET',
    path: '/api/admin/accounts',
    context: 'admin',
    summary: 'List accounts with their role, status and sign-in provenance',
    request: admin.ListAdminAccountsRequestSchema,
    response: admin.ListAdminAccountsResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
  }),
  adminSetAccountRole: route({
    operationId: 'adminSetAccountRole',
    method: 'PUT',
    path: '/api/admin/accounts/:userId/role',
    context: 'admin',
    summary: "Change one account's role",
    request: admin.SetAccountRoleRequestSchema,
    response: admin.SetAccountRoleResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    /* CONFLICT is the last-administrator refusal: an installation with no ADMIN
       can never grant one back, so the platform will not let the final one go. */
    errors: ['NOT_FOUND', 'VALIDATION_FAILED', 'CONFLICT'],
  }),
  adminListRoleAssignments: route({
    operationId: 'adminListRoleAssignments',
    method: 'GET',
    path: '/api/admin/role-assignments',
    context: 'admin',
    summary: 'List the roles decided for addresses, applied or still pending',
    request: admin.ListRoleAssignmentsRequestSchema,
    response: admin.ListRoleAssignmentsResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
  }),
  adminPutRoleAssignment: route({
    operationId: 'adminPutRoleAssignment',
    method: 'PUT',
    path: '/api/admin/role-assignments',
    context: 'admin',
    summary: 'Decide the role an address gets, now or on its first sign-in',
    request: admin.PutRoleAssignmentRequestSchema,
    response: admin.PutRoleAssignmentResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    errors: ['VALIDATION_FAILED', 'CONFLICT'],
  }),
  adminDeleteRoleAssignment: route({
    operationId: 'adminDeleteRoleAssignment',
    method: 'DELETE',
    path: '/api/admin/role-assignments/:assignmentId',
    context: 'admin',
    summary: 'Withdraw a pending role assignment',
    request: admin.DeleteRoleAssignmentRequestSchema,
    response: admin.DeleteRoleAssignmentResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),

  /* ── admin: the method purge ──────────────────────────────────────────────
     The only way a PUBLISHED method leaves this platform, and the most
     destructive operation in the manifest. `methodsDelete` above refuses a
     published version by design and always will; this is a separate operation
     under `/api/admin` because it is a different act by a different authority,
     not a flag on the ordinary one.

     TWO ROUTES, ON PURPOSE. The GET is a preview that counts everything the
     purge would take — including the workshop sessions and baselined plans that
     belong to other people — and it changes nothing, so an administrator can
     look before deciding. The DELETE carries `confirmMethodId` and re-checks it
     server-side against the loaded version, so a stale table row or a
     mis-copied id cannot destroy a method nobody was looking at. Both are
     ADMIN_ONLY at the transport (RolesGuard) and re-check `admin:manage` in
     their use case; the destruction happens in one transaction and writes an
     audit row that is the only record left afterwards. */
  adminPreviewMethodPurge: route({
    operationId: 'adminPreviewMethodPurge',
    method: 'GET',
    path: '/api/admin/methods/:methodVersionId/purge',
    context: 'admin',
    summary: 'Count everything an administrative purge of this method would destroy',
    request: admin.PreviewMethodPurgeRequestSchema,
    response: admin.PreviewMethodPurgeResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  adminPurgeMethod: route({
    operationId: 'adminPurgeMethod',
    method: 'DELETE',
    path: '/api/admin/methods/:methodVersionId/purge',
    context: 'admin',
    summary: 'Destroy a method version, its variants, and every session and plan drawn from them',
    request: admin.PurgeMethodRequestSchema,
    response: admin.PurgeMethodResponseSchema,
    roles: ADMIN_ONLY,
    successStatus: 200,
    /* VALIDATION_FAILED is the second-key refusal: `confirmMethodId` did not
       match the version at that id. Not CONFLICT — nothing about the method's
       state can block a purge, which is the entire point of having one. */
    errors: ['NOT_FOUND', 'VALIDATION_FAILED'],
  }),

  /* ── methods (authoring lifecycle) ────────────────────────────────────── */
  methodsList: route({
    operationId: 'methodsList',
    method: 'GET',
    path: '/api/methods',
    context: 'methods',
    summary: 'List method versions',
    request: methods.ListMethodsRequestSchema,
    response: methods.ListMethodsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  methodsCreate: route({
    operationId: 'methodsCreate',
    method: 'POST',
    path: '/api/methods',
    context: 'methods',
    summary: 'Create a draft method version',
    request: methods.CreateMethodRequestSchema,
    response: methods.CreateMethodResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  methodsGet: route({
    operationId: 'methodsGet',
    method: 'GET',
    path: '/api/methods/:methodVersionId',
    context: 'methods',
    summary: 'Read one method version',
    request: methods.GetMethodRequestSchema,
    response: methods.GetMethodResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  /**
   * Content editing at the method level, mirroring `graphUpdateElement` one level
   * up. Separate from `methodsCreate` because the four fields it touches are
   * authored over the life of a draft, not guessed at the moment it is created.
   */
  methodsUpdate: route({
    operationId: 'methodsUpdate',
    method: 'PATCH',
    path: '/api/methods/:methodVersionId',
    context: 'methods',
    summary: "Edit a method version's name, description, writeup or tags",
    request: methods.UpdateMethodRequestSchema,
    response: methods.UpdateMethodResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  methodsDeriveVariant: route({
    operationId: 'methodsDeriveVariant',
    method: 'POST',
    path: '/api/methods/:methodVersionId/variants',
    context: 'methods',
    summary: 'Derive a new draft variant from a method version',
    request: methods.DeriveVariantRequestSchema,
    response: methods.DeriveVariantResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'ALREADY_EXISTS'],
  }),
  methodsDelete: route({
    operationId: 'methodsDelete',
    method: 'DELETE',
    path: '/api/methods/:methodVersionId',
    context: 'methods',
    summary: 'Delete a method version that nothing else depends on',
    request: methods.DeleteMethodRequestSchema,
    response: methods.DeleteMethodResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    /* CONFLICT is the refusal when something depends on it — published, derived from, or
       used by a workshop. Not INVALID_STATE: the version's own state is only one of the
       three reasons, and the other two are about what points at it. */
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  methodsSubmitForReview: route({
    operationId: 'methodsSubmitForReview',
    method: 'POST',
    path: '/api/methods/:methodVersionId/submit',
    context: 'methods',
    summary: 'Submit a draft for review',
    request: methods.SubmitForReviewRequestSchema,
    response: methods.SubmitForReviewResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ONTOLOGY_INVALID'],
  }),
  methodsReview: route({
    operationId: 'methodsReview',
    method: 'POST',
    path: '/api/methods/:methodVersionId/review',
    context: 'methods',
    summary: 'Approve or reject a method version under review',
    request: methods.ReviewMethodRequestSchema,
    response: methods.ReviewMethodResponseSchema,
    roles: REVIEWERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  methodsPublish: route({
    operationId: 'methodsPublish',
    method: 'POST',
    path: '/api/methods/:methodVersionId/publish',
    context: 'methods',
    summary: 'Publish an approved method version (immutable thereafter)',
    request: methods.PublishMethodRequestSchema,
    response: methods.PublishMethodResponseSchema,
    roles: REVIEWERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ONTOLOGY_INVALID'],
  }),

  /* ── method-graph (element-scoped commands) ───────────────────────────── */
  graphGet: route({
    operationId: 'graphGet',
    method: 'GET',
    path: '/api/methods/:methodVersionId/graph',
    context: 'method-graph',
    summary: 'Read the whole method graph plus current locks',
    request: graph.GetGraphRequestSchema,
    response: graph.GetGraphResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  graphCreateElement: route({
    operationId: 'graphCreateElement',
    method: 'POST',
    path: '/api/methods/:methodVersionId/graph/elements',
    context: 'method-graph',
    summary: 'Create a phase, stage, work practice, activity, role, gate, lens or ceremony',
    request: graph.CreateElementRequestSchema,
    response: graph.CreateElementResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ELEMENT_LOCKED', 'ALREADY_EXISTS'],
  }),
  /**
   * Content editing, distinct from `graphRenameElement` and `graphMoveElement`
   * because the three are different authorial acts: the tree does inline renames,
   * the canvas does drags, and the detail panel edits writeups. Sharing one route
   * would mean a drag had to resend the whole writeup, and a 409 on either would
   * lose the other.
   */
  graphUpdateElement: route({
    operationId: 'graphUpdateElement',
    method: 'PATCH',
    path: '/api/methods/:methodVersionId/graph/elements/:elementId',
    context: 'method-graph',
    summary: "Edit one graph element's content fields",
    request: graph.UpdateElementRequestSchema,
    response: graph.UpdateElementResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ELEMENT_LOCKED'],
  }),
  graphRenameElement: route({
    operationId: 'graphRenameElement',
    method: 'PATCH',
    path: '/api/methods/:methodVersionId/graph/elements/:elementId/name',
    context: 'method-graph',
    summary: 'Rename one graph element',
    request: graph.RenameElementRequestSchema,
    response: graph.RenameElementResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ELEMENT_LOCKED'],
  }),
  /**
   * Separate from `graphRenameElement` because a label and an identity are not the
   * same thing: the name is what a reader sees, the id is what a published document,
   * a workshop decision or a plan task cites. `ALREADY_EXISTS` is the new id taken;
   * `CONFLICT` is another context still citing the old one, cleared with `force`.
   */
  graphChangeElementId: route({
    operationId: 'graphChangeElementId',
    method: 'PATCH',
    path: '/api/methods/:methodVersionId/graph/elements/:elementId/identifier',
    context: 'method-graph',
    summary: "Change one graph element's canonical ontology id",
    request: graph.ChangeElementIdRequestSchema,
    response: graph.ChangeElementIdResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: [
      'NOT_FOUND',
      'INVALID_STATE',
      'REVISION_CONFLICT',
      'ELEMENT_LOCKED',
      'ALREADY_EXISTS',
      'CONFLICT',
    ],
  }),
  graphMoveElement: route({
    operationId: 'graphMoveElement',
    method: 'PATCH',
    path: '/api/methods/:methodVersionId/graph/elements/:elementId/position',
    context: 'method-graph',
    summary: 'Re-parent and/or re-order one graph element',
    request: graph.MoveElementRequestSchema,
    response: graph.MoveElementResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ELEMENT_LOCKED', 'CONFLICT'],
  }),
  graphDeleteElement: route({
    operationId: 'graphDeleteElement',
    method: 'DELETE',
    path: '/api/methods/:methodVersionId/graph/elements/:elementId',
    context: 'method-graph',
    summary: 'Delete one graph element',
    request: graph.DeleteElementRequestSchema,
    response: graph.DeleteElementResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ELEMENT_LOCKED', 'CONFLICT'],
  }),
  graphAddDependencyEdge: route({
    operationId: 'graphAddDependencyEdge',
    method: 'POST',
    path: '/api/methods/:methodVersionId/graph/edges',
    context: 'method-graph',
    summary: 'Add a precedes / dependsOn / loopsTo edge',
    request: graph.AddDependencyEdgeRequestSchema,
    response: graph.AddDependencyEdgeResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'CONFLICT', 'ONTOLOGY_INVALID'],
  }),
  graphRemoveDependencyEdge: route({
    operationId: 'graphRemoveDependencyEdge',
    method: 'DELETE',
    path: '/api/methods/:methodVersionId/graph/edges',
    context: 'method-graph',
    summary: 'Remove a dependency edge',
    request: graph.RemoveDependencyEdgeRequestSchema,
    response: graph.RemoveDependencyEdgeResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  graphBindResource: route({
    operationId: 'graphBindResource',
    method: 'POST',
    path: '/api/methods/:methodVersionId/graph/activities/:activityId/bindings',
    context: 'method-graph',
    summary: 'Bind a deliverable, tool, asset, agent, skill or MCP server to an activity',
    request: graph.BindResourceRequestSchema,
    response: graph.BindResourceResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'ALREADY_EXISTS'],
  }),
  graphUnbindResource: route({
    operationId: 'graphUnbindResource',
    method: 'DELETE',
    path: '/api/methods/:methodVersionId/graph/activities/:activityId/bindings/:bindingKind/:targetId',
    context: 'method-graph',
    summary: 'Remove a binding from an activity',
    request: graph.UnbindResourceRequestSchema,
    response: graph.UnbindResourceResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  graphAcquireElementLock: route({
    operationId: 'graphAcquireElementLock',
    method: 'POST',
    path: '/api/methods/:methodVersionId/graph/locks',
    context: 'method-graph',
    summary: 'Acquire the soft edit lock on one element',
    request: graph.AcquireElementLockRequestSchema,
    response: graph.AcquireElementLockResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'ELEMENT_LOCKED', 'INVALID_STATE'],
  }),
  graphReleaseElementLock: route({
    operationId: 'graphReleaseElementLock',
    method: 'DELETE',
    path: '/api/methods/:methodVersionId/graph/locks/:elementId',
    context: 'method-graph',
    summary: 'Release an edit lock the caller holds',
    request: graph.ReleaseElementLockRequestSchema,
    response: graph.ReleaseElementLockResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'FORBIDDEN'],
  }),
  graphListElementLocks: route({
    operationId: 'graphListElementLocks',
    method: 'GET',
    path: '/api/methods/:methodVersionId/graph/locks',
    context: 'method-graph',
    summary: 'List active edit locks on a method version',
    request: graph.ListElementLocksRequestSchema,
    response: graph.ListElementLocksResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),

  /* ── validation ───────────────────────────────────────────────────────── */
  validationRun: route({
    operationId: 'validationRun',
    method: 'POST',
    path: '/api/methods/:methodVersionId/validate',
    context: 'validation',
    summary: 'Run the ontology rule catalogue against a method version',
    request: validation.RunValidationRequestSchema,
    response: validation.RunValidationResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  validationListRules: route({
    operationId: 'validationListRules',
    method: 'GET',
    path: '/api/validation/rules',
    context: 'validation',
    summary: 'The validation rule catalogue',
    request: validation.ListRulesRequestSchema,
    response: validation.ListRulesResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),

  /* ── catalog ──────────────────────────────────────────────────────────── */
  catalogListTools: route({
    operationId: 'catalogListTools',
    method: 'GET',
    path: '/api/catalog/tools',
    context: 'catalog',
    summary: 'List tools',
    request: catalog.ListToolsRequestSchema,
    response: catalog.ListToolsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  catalogCreateTool: route({
    operationId: 'catalogCreateTool',
    method: 'POST',
    path: '/api/catalog/tools',
    context: 'catalog',
    summary: 'Create a tool',
    request: catalog.CreateToolRequestSchema,
    response: catalog.CreateToolResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  catalogGetTool: route({
    operationId: 'catalogGetTool',
    method: 'GET',
    path: '/api/catalog/tools/:toolId',
    context: 'catalog',
    summary: 'Read a tool',
    request: catalog.GetToolRequestSchema,
    response: catalog.GetToolResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogUpdateTool: route({
    operationId: 'catalogUpdateTool',
    method: 'PUT',
    path: '/api/catalog/tools/:toolId',
    context: 'catalog',
    summary: 'Replace a tool',
    request: catalog.UpdateToolRequestSchema,
    response: catalog.UpdateToolResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogDeleteTool: route({
    operationId: 'catalogDeleteTool',
    method: 'DELETE',
    path: '/api/catalog/tools/:toolId',
    context: 'catalog',
    summary: 'Delete a tool',
    request: catalog.DeleteToolRequestSchema,
    response: catalog.DeleteToolResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  catalogListAssets: route({
    operationId: 'catalogListAssets',
    method: 'GET',
    path: '/api/catalog/assets',
    context: 'catalog',
    summary: 'List assets',
    request: catalog.ListAssetsRequestSchema,
    response: catalog.ListAssetsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  catalogCreateAsset: route({
    operationId: 'catalogCreateAsset',
    method: 'POST',
    path: '/api/catalog/assets',
    context: 'catalog',
    summary: 'Create an asset',
    request: catalog.CreateAssetRequestSchema,
    response: catalog.CreateAssetResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  catalogGetAsset: route({
    operationId: 'catalogGetAsset',
    method: 'GET',
    path: '/api/catalog/assets/:assetId',
    context: 'catalog',
    summary: 'Read an asset',
    request: catalog.GetAssetRequestSchema,
    response: catalog.GetAssetResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogUpdateAsset: route({
    operationId: 'catalogUpdateAsset',
    method: 'PUT',
    path: '/api/catalog/assets/:assetId',
    context: 'catalog',
    summary: 'Replace an asset',
    request: catalog.UpdateAssetRequestSchema,
    response: catalog.UpdateAssetResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogDeleteAsset: route({
    operationId: 'catalogDeleteAsset',
    method: 'DELETE',
    path: '/api/catalog/assets/:assetId',
    context: 'catalog',
    summary: 'Delete an asset',
    request: catalog.DeleteAssetRequestSchema,
    response: catalog.DeleteAssetResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  catalogListAgents: route({
    operationId: 'catalogListAgents',
    method: 'GET',
    path: '/api/catalog/agents',
    context: 'catalog',
    summary: 'List agents',
    request: catalog.ListAgentsRequestSchema,
    response: catalog.ListAgentsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  catalogCreateAgent: route({
    operationId: 'catalogCreateAgent',
    method: 'POST',
    path: '/api/catalog/agents',
    context: 'catalog',
    summary: 'Create an agent',
    request: catalog.CreateAgentRequestSchema,
    response: catalog.CreateAgentResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  catalogGetAgent: route({
    operationId: 'catalogGetAgent',
    method: 'GET',
    path: '/api/catalog/agents/:agentId',
    context: 'catalog',
    summary: 'Read an agent',
    request: catalog.GetAgentRequestSchema,
    response: catalog.GetAgentResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogUpdateAgent: route({
    operationId: 'catalogUpdateAgent',
    method: 'PUT',
    path: '/api/catalog/agents/:agentId',
    context: 'catalog',
    summary: 'Replace an agent',
    request: catalog.UpdateAgentRequestSchema,
    response: catalog.UpdateAgentResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogDeleteAgent: route({
    operationId: 'catalogDeleteAgent',
    method: 'DELETE',
    path: '/api/catalog/agents/:agentId',
    context: 'catalog',
    summary: 'Delete an agent',
    request: catalog.DeleteAgentRequestSchema,
    response: catalog.DeleteAgentResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  catalogListAgentSkills: route({
    operationId: 'catalogListAgentSkills',
    method: 'GET',
    path: '/api/catalog/agent-skills',
    context: 'catalog',
    summary: 'List agent skills',
    request: catalog.ListAgentSkillsRequestSchema,
    response: catalog.ListAgentSkillsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  catalogCreateAgentSkill: route({
    operationId: 'catalogCreateAgentSkill',
    method: 'POST',
    path: '/api/catalog/agent-skills',
    context: 'catalog',
    summary: 'Create an agent skill',
    request: catalog.CreateAgentSkillRequestSchema,
    response: catalog.CreateAgentSkillResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  catalogGetAgentSkill: route({
    operationId: 'catalogGetAgentSkill',
    method: 'GET',
    path: '/api/catalog/agent-skills/:agentSkillId',
    context: 'catalog',
    summary: 'Read an agent skill',
    request: catalog.GetAgentSkillRequestSchema,
    response: catalog.GetAgentSkillResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogUpdateAgentSkill: route({
    operationId: 'catalogUpdateAgentSkill',
    method: 'PUT',
    path: '/api/catalog/agent-skills/:agentSkillId',
    context: 'catalog',
    summary: 'Replace an agent skill',
    request: catalog.UpdateAgentSkillRequestSchema,
    response: catalog.UpdateAgentSkillResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogDeleteAgentSkill: route({
    operationId: 'catalogDeleteAgentSkill',
    method: 'DELETE',
    path: '/api/catalog/agent-skills/:agentSkillId',
    context: 'catalog',
    summary: 'Delete an agent skill',
    request: catalog.DeleteAgentSkillRequestSchema,
    response: catalog.DeleteAgentSkillResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  catalogListMcpServers: route({
    operationId: 'catalogListMcpServers',
    method: 'GET',
    path: '/api/catalog/mcp-servers',
    context: 'catalog',
    summary: 'List MCP servers',
    request: catalog.ListMcpServersRequestSchema,
    response: catalog.ListMcpServersResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  catalogCreateMcpServer: route({
    operationId: 'catalogCreateMcpServer',
    method: 'POST',
    path: '/api/catalog/mcp-servers',
    context: 'catalog',
    summary: 'Create an MCP server entry',
    request: catalog.CreateMcpServerRequestSchema,
    response: catalog.CreateMcpServerResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  catalogGetMcpServer: route({
    operationId: 'catalogGetMcpServer',
    method: 'GET',
    path: '/api/catalog/mcp-servers/:mcpServerId',
    context: 'catalog',
    summary: 'Read an MCP server entry',
    request: catalog.GetMcpServerRequestSchema,
    response: catalog.GetMcpServerResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogUpdateMcpServer: route({
    operationId: 'catalogUpdateMcpServer',
    method: 'PUT',
    path: '/api/catalog/mcp-servers/:mcpServerId',
    context: 'catalog',
    summary: 'Replace an MCP server entry',
    request: catalog.UpdateMcpServerRequestSchema,
    response: catalog.UpdateMcpServerResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  catalogDeleteMcpServer: route({
    operationId: 'catalogDeleteMcpServer',
    method: 'DELETE',
    path: '/api/catalog/mcp-servers/:mcpServerId',
    context: 'catalog',
    summary: 'Delete an MCP server entry',
    request: catalog.DeleteMcpServerRequestSchema,
    response: catalog.DeleteMcpServerResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  catalogSearch: route({
    operationId: 'catalogSearch',
    method: 'GET',
    path: '/api/catalog/search',
    context: 'catalog',
    summary: 'Search every catalog kind at once, with per-kind counts',
    request: catalog.SearchCatalogRequestSchema,
    response: catalog.SearchCatalogResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),

  /* ── session catalogue ────────────────────────────────────────────────────
   *
   * One adoption's own catalogue. Declared in the `catalog` context and served under
   * `/workshops/:sessionId` because the two facts are separate: the resource is a
   * catalogue entry, and the session is what owns the scope and authorises the write.
   *
   * Reading is `ANY_ROLE`, exactly as the platform catalogue is. A reviewer looking at a
   * tailored variant has to be able to see which entries the client brought, or the
   * compare surface shows nodes with no origin.
   */
  sessionCatalogueList: route({
    operationId: 'sessionCatalogueList',
    method: 'GET',
    path: '/api/workshops/:sessionId/catalogue',
    context: 'catalog',
    summary: "The entries a session's client brought, with per-kind counts",
    request: catalog.ListSessionCatalogueRequestSchema,
    response: catalog.ListSessionCatalogueResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  sessionCatalogueAdd: route({
    operationId: 'sessionCatalogueAdd',
    method: 'POST',
    path: '/api/workshops/:sessionId/catalogue',
    context: 'catalog',
    summary: "Create an entry in this session's catalogue",
    request: catalog.AddSessionCatalogueEntryRequestSchema,
    response: catalog.SessionCatalogueEntryResponseSchema,
    roles: SESSION_CATALOGUERS,
    successStatus: 201,
    // INVALID_STATE: a session that has already published its variant does not gain
    // entries — the variant froze what it quoted, so a later entry could only mislead.
    errors: ['NOT_FOUND', 'ALREADY_EXISTS', 'INVALID_STATE'],
  }),
  sessionCatalogueBring: route({
    operationId: 'sessionCatalogueBring',
    method: 'POST',
    path: '/api/workshops/:sessionId/catalogue/bring',
    context: 'catalog',
    summary: 'Copy a platform entry into this session as a client-owned stand-in',
    request: catalog.BringGlobalEntryRequestSchema,
    response: catalog.SessionCatalogueEntryResponseSchema,
    roles: SESSION_CATALOGUERS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'ALREADY_EXISTS', 'INVALID_STATE'],
  }),
  sessionCatalogueUpdate: route({
    operationId: 'sessionCatalogueUpdate',
    method: 'PUT',
    path: '/api/workshops/:sessionId/catalogue/:kind/:recordId',
    context: 'catalog',
    summary: "Replace an entry in this session's catalogue",
    request: catalog.UpdateSessionCatalogueEntryRequestSchema,
    response: catalog.SessionCatalogueEntryResponseSchema,
    roles: SESSION_CATALOGUERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE'],
  }),
  sessionCatalogueRemove: route({
    operationId: 'sessionCatalogueRemove',
    method: 'DELETE',
    path: '/api/workshops/:sessionId/catalogue/:kind/:recordId',
    context: 'catalog',
    summary: "Remove an entry from this session's catalogue",
    request: catalog.RemoveSessionCatalogueEntryRequestSchema,
    response: catalog.DeleteCatalogEntryResponseSchema,
    roles: SESSION_CATALOGUERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT', 'INVALID_STATE'],
  }),

  /* ── catalogue promotion ──────────────────────────────────────────────────
   *
   * The one path from a session's catalogue into the platform's, and it is two-sided on
   * purpose: `SESSION_CATALOGUERS` propose, `AUTHORS` decide. A facilitator who could
   * accept their own proposal would be able to grow the global catalogue as a side effect
   * of running a workshop, which is exactly the authority `catalog:manage` withholds.
   *
   * The proposal is addressed like every other session entry — `:kind/:recordId`, because
   * a session entry's ontology id carries a `/` — and the decisions are addressed by the
   * proposal's own uuid, since by then the queue is a platform-wide list and the session
   * it came from is a column rather than a path.
   */
  sessionCataloguePropose: route({
    operationId: 'sessionCataloguePropose',
    method: 'POST',
    path: '/api/workshops/:sessionId/catalogue/:kind/:recordId/promotion',
    context: 'catalog',
    summary: 'Propose a session entry for the platform catalogue',
    request: catalog.ProposeCataloguePromotionRequestSchema,
    response: catalog.ProposeCataloguePromotionResponseSchema,
    roles: SESSION_CATALOGUERS,
    successStatus: 201,
    // ALREADY_EXISTS: one open proposal per entry. CONFLICT: the entry is a shadow of a
    // platform row, so there is nothing to promote — the platform already has it.
    errors: ['NOT_FOUND', 'ALREADY_EXISTS', 'CONFLICT', 'INVALID_STATE'],
  }),
  catalogListPromotions: route({
    operationId: 'catalogListPromotions',
    method: 'GET',
    path: '/api/catalog/promotions',
    context: 'catalog',
    summary: 'The promotion queue, with counts per state',
    request: catalog.ListCataloguePromotionsRequestSchema,
    response: catalog.ListCataloguePromotionsResponseSchema,
    // AUTHORS, not ANY_ROLE, unlike every other catalog read. The queue is a list of
    // arguments people made about each other's work, and it is only actionable by the two
    // roles that can answer it.
    roles: AUTHORS,
    successStatus: 200,
  }),
  catalogAcceptPromotion: route({
    operationId: 'catalogAcceptPromotion',
    method: 'POST',
    path: '/api/catalog/promotions/:promotionId/accept',
    context: 'catalog',
    summary: 'Accept a proposal: write the platform entry and rehome its id',
    request: catalog.AcceptCataloguePromotionRequestSchema,
    response: catalog.AcceptCataloguePromotionResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    // ALREADY_EXISTS: the rehomed id is taken in the platform catalogue and the author has
    // to choose another. CONFLICT: the proposal was already decided.
    errors: ['NOT_FOUND', 'ALREADY_EXISTS', 'CONFLICT'],
  }),
  catalogDeclinePromotion: route({
    operationId: 'catalogDeclinePromotion',
    method: 'POST',
    path: '/api/catalog/promotions/:promotionId/decline',
    context: 'catalog',
    summary: 'Decline a proposal, with the reason it did not carry',
    request: catalog.DeclineCataloguePromotionRequestSchema,
    response: catalog.DeclineCataloguePromotionResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),

  /* ── deliverables ─────────────────────────────────────────────────────── */
  deliverablesList: route({
    operationId: 'deliverablesList',
    method: 'GET',
    path: '/api/deliverables',
    context: 'deliverables',
    summary: 'List deliverables',
    request: deliverables.ListDeliverablesRequestSchema,
    response: deliverables.ListDeliverablesResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  deliverablesCreate: route({
    operationId: 'deliverablesCreate',
    method: 'POST',
    path: '/api/deliverables',
    context: 'deliverables',
    summary: 'Create a deliverable, optionally seeding template version 1',
    request: deliverables.CreateDeliverableRequestSchema,
    response: deliverables.CreateDeliverableResponseSchema,
    roles: AUTHORS,
    successStatus: 201,
    errors: ['ALREADY_EXISTS'],
  }),
  deliverablesGet: route({
    operationId: 'deliverablesGet',
    method: 'GET',
    path: '/api/deliverables/:deliverableId',
    context: 'deliverables',
    summary: 'Read a deliverable and optionally its current body',
    request: deliverables.GetDeliverableRequestSchema,
    response: deliverables.GetDeliverableResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  deliverablesUpdate: route({
    operationId: 'deliverablesUpdate',
    method: 'PUT',
    path: '/api/deliverables/:deliverableId',
    context: 'deliverables',
    summary: 'Replace deliverable metadata (body edits create a version)',
    request: deliverables.UpdateDeliverableRequestSchema,
    response: deliverables.UpdateDeliverableResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  deliverablesDelete: route({
    operationId: 'deliverablesDelete',
    method: 'DELETE',
    path: '/api/deliverables/:deliverableId',
    context: 'deliverables',
    summary: 'Delete a deliverable',
    request: deliverables.DeleteDeliverableRequestSchema,
    response: deliverables.DeleteDeliverableResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT', 'IMMUTABLE'],
  }),
  deliverablesListVersions: route({
    operationId: 'deliverablesListVersions',
    method: 'GET',
    path: '/api/deliverables/:deliverableId/versions',
    context: 'deliverables',
    summary: 'List template versions of a deliverable',
    request: deliverables.ListTemplateVersionsRequestSchema,
    response: deliverables.ListTemplateVersionsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  deliverablesCreateVersion: route({
    operationId: 'deliverablesCreateVersion',
    method: 'POST',
    path: '/api/deliverables/:deliverableId/versions',
    context: 'deliverables',
    summary: 'Create the next template version',
    request: deliverables.CreateTemplateVersionRequestSchema,
    response: deliverables.CreateTemplateVersionResponseSchema,
    roles: AUTHORS_AND_FACILITATORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'CONFLICT', 'IMMUTABLE'],
  }),
  deliverablesGetVersion: route({
    operationId: 'deliverablesGetVersion',
    method: 'GET',
    path: '/api/deliverables/:deliverableId/versions/:versionNumber',
    context: 'deliverables',
    summary: 'Read one template version body',
    request: deliverables.GetTemplateVersionRequestSchema,
    response: deliverables.GetTemplateVersionResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),

  /* ── publishing ───────────────────────────────────────────────────────── */
  publishingExport: route({
    operationId: 'publishingExport',
    method: 'GET',
    path: '/api/methods/:methodVersionId/export',
    context: 'publishing',
    summary: 'Export a method version as JSON-LD',
    request: publishing.ExportMethodRequestSchema,
    response: publishing.ExportMethodResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  publishingImport: route({
    operationId: 'publishingImport',
    method: 'POST',
    path: '/api/methods/import',
    context: 'publishing',
    summary: 'Import a JSON-LD method document, or dry-run validate it',
    request: publishing.ImportMethodRequestSchema,
    response: publishing.ImportMethodResponseSchema,
    roles: AUTHORS,
    successStatus: 200,
    errors: ['IMPORT_FAILED', 'ONTOLOGY_INVALID', 'PAYLOAD_TOO_LARGE'],
  }),
  publishingListPublished: route({
    operationId: 'publishingListPublished',
    method: 'GET',
    path: '/api/published',
    context: 'publishing',
    summary: 'List published methods',
    request: publishing.ListPublishedRequestSchema,
    response: publishing.ListPublishedResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  publishingCompare: route({
    operationId: 'publishingCompare',
    method: 'GET',
    path: '/api/compare',
    context: 'publishing',
    summary: 'Compare any two method versions element by element',
    request: publishing.CompareMethodsRequestSchema,
    response: publishing.CompareMethodsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),

  /* ── workshop ─────────────────────────────────────────────────────────── */
  workshopList: route({
    operationId: 'workshopList',
    method: 'GET',
    path: '/api/workshops',
    context: 'workshop',
    summary: 'List workshop sessions',
    request: workshop.ListWorkshopSessionsRequestSchema,
    response: workshop.ListWorkshopSessionsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  workshopCreate: route({
    operationId: 'workshopCreate',
    method: 'POST',
    path: '/api/workshops',
    context: 'workshop',
    summary: 'Create a workshop session over a method version',
    request: workshop.CreateWorkshopSessionRequestSchema,
    response: workshop.CreateWorkshopSessionResponseSchema,
    roles: FACILITATORS,
    successStatus: 201,
    errors: ['NOT_FOUND'],
  }),
  workshopGet: route({
    operationId: 'workshopGet',
    method: 'GET',
    path: '/api/workshops/:sessionId',
    context: 'workshop',
    summary: 'Read a workshop session with decisions and overrides',
    request: workshop.GetWorkshopSessionRequestSchema,
    response: workshop.GetWorkshopSessionResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  workshopAddParticipant: route({
    operationId: 'workshopAddParticipant',
    method: 'POST',
    path: '/api/workshops/:sessionId/participants',
    context: 'workshop',
    summary: 'Add a participant to a session',
    request: workshop.AddParticipantRequestSchema,
    response: workshop.AddParticipantResponseSchema,
    roles: FACILITATORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'ALREADY_EXISTS', 'REVISION_CONFLICT'],
  }),
  workshopRecordDecision: route({
    operationId: 'workshopRecordDecision',
    method: 'PUT',
    path: '/api/workshops/:sessionId/decisions/:elementId',
    context: 'workshop',
    summary: 'Record an INCLUDE / ADAPT / SKIP decision for one element',
    request: workshop.RecordTailoringDecisionRequestSchema,
    response: workshop.RecordTailoringDecisionResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  workshopOverrideTemplate: route({
    operationId: 'workshopOverrideTemplate',
    method: 'PUT',
    path: '/api/workshops/:sessionId/template-overrides/:deliverableId',
    context: 'workshop',
    summary: 'Override a deliverable template body inside the session',
    request: workshop.OverrideTemplateBodyRequestSchema,
    response: workshop.OverrideTemplateBodyResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'CONFLICT'],
  }),
  workshopSetElementOverride: route({
    operationId: 'workshopSetElementOverride',
    method: 'PUT',
    path: '/api/workshops/:sessionId/element-overrides/:elementId',
    context: 'workshop',
    summary: "Override an element's name and fields for the client",
    request: workshop.SetElementOverrideRequestSchema,
    response: workshop.SetElementOverrideResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'CONFLICT'],
  }),
  workshopClearElementOverride: route({
    operationId: 'workshopClearElementOverride',
    method: 'DELETE',
    path: '/api/workshops/:sessionId/element-overrides/:elementId',
    context: 'workshop',
    summary: "Drop an element's field override, restoring the baseline",
    request: workshop.ClearElementOverrideRequestSchema,
    response: workshop.ClearElementOverrideResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  workshopSetBindingOverride: route({
    operationId: 'workshopSetBindingOverride',
    method: 'PUT',
    path: '/api/workshops/:sessionId/binding-overrides/:activityId',
    context: 'workshop',
    summary: 'Add, remove or forget one activity binding for the client',
    request: workshop.SetBindingOverrideRequestSchema,
    response: workshop.SetBindingOverrideResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'CONFLICT'],
  }),
  workshopSetParameter: route({
    operationId: 'workshopSetParameter',
    method: 'PUT',
    path: '/api/workshops/:sessionId/parameters/:key',
    context: 'workshop',
    summary: 'Declare or answer a method parameter for the client',
    request: workshop.SetMethodParameterRequestSchema,
    response: workshop.SetMethodParameterResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'VALIDATION_FAILED'],
  }),
  workshopDeleteParameter: route({
    operationId: 'workshopDeleteParameter',
    method: 'DELETE',
    path: '/api/workshops/:sessionId/parameters/:key',
    context: 'workshop',
    summary: 'Remove a method parameter from the session',
    request: workshop.DeleteMethodParameterRequestSchema,
    response: workshop.DeleteMethodParameterResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  workshopComplete: route({
    operationId: 'workshopComplete',
    method: 'POST',
    path: '/api/workshops/:sessionId/complete',
    context: 'workshop',
    summary: 'Complete a session and report undecided elements',
    request: workshop.CompleteWorkshopSessionRequestSchema,
    response: workshop.CompleteWorkshopSessionResponseSchema,
    roles: FACILITATORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  workshopPublishVariant: route({
    operationId: 'workshopPublishVariant',
    method: 'POST',
    path: '/api/workshops/:sessionId/publish',
    context: 'workshop',
    summary: 'Publish the tailored variant produced by a session',
    request: workshop.PublishTailoredVariantRequestSchema,
    response: workshop.PublishTailoredVariantResponseSchema,
    roles: FACILITATORS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'ONTOLOGY_INVALID', 'REVISION_CONFLICT'],
  }),

  /* ── planning ─────────────────────────────────────────────────────────── */
  planTemplatesList: route({
    operationId: 'planTemplatesList',
    method: 'GET',
    path: '/api/plan-templates',
    context: 'planning',
    summary: 'List the built-in plan templates',
    request: planning.ListPlanTemplatesRequestSchema,
    response: planning.ListPlanTemplatesResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  planTemplatesGet: route({
    operationId: 'planTemplatesGet',
    method: 'GET',
    path: '/api/plan-templates/:templateKey',
    context: 'planning',
    summary: 'Read one plan template, with its parameters and scaffold',
    request: planning.GetPlanTemplateRequestSchema,
    response: planning.GetPlanTemplateResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  plansList: route({
    operationId: 'plansList',
    method: 'GET',
    path: '/api/plans',
    context: 'planning',
    summary: 'List plans',
    request: planning.ListPlansRequestSchema,
    response: planning.ListPlansResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  plansGet: route({
    operationId: 'plansGet',
    method: 'GET',
    path: '/api/plans/:planId',
    context: 'planning',
    summary: 'Read one plan: its work breakdown, dependencies and generation report',
    request: planning.GetPlanRequestSchema,
    response: planning.GetPlanResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  plansGenerate: route({
    operationId: 'plansGenerate',
    method: 'POST',
    path: '/api/plans/generate',
    context: 'planning',
    summary: 'Generate a work breakdown from a method version and a plan template',
    request: planning.GeneratePlanRequestSchema,
    response: planning.GeneratePlanResponseSchema,
    roles: PLANNERS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'VALIDATION_FAILED', 'ONTOLOGY_INVALID', 'CONFLICT'],
  }),
  plansRegenerate: route({
    operationId: 'plansRegenerate',
    method: 'POST',
    path: '/api/plans/:planId/regenerate',
    context: 'planning',
    summary: 'Replay the generator over an existing plan, keeping curated work',
    request: planning.RegeneratePlanRequestSchema,
    response: planning.RegeneratePlanResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'VALIDATION_FAILED'],
  }),
  plansUpdate: route({
    operationId: 'plansUpdate',
    method: 'PATCH',
    path: '/api/plans/:planId',
    context: 'planning',
    summary: 'Rename a plan, move its start date or change its working week',
    request: planning.UpdatePlanRequestSchema,
    response: planning.UpdatePlanResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  plansBaseline: route({
    operationId: 'plansBaseline',
    method: 'POST',
    path: '/api/plans/:planId/baseline',
    context: 'planning',
    summary: 'Freeze the plan as agreed and report what is still outstanding',
    request: planning.BaselinePlanRequestSchema,
    response: planning.BaselinePlanResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  plansArchive: route({
    operationId: 'plansArchive',
    method: 'POST',
    path: '/api/plans/:planId/archive',
    context: 'planning',
    summary: 'Archive a plan nobody is working to',
    request: planning.ArchivePlanRequestSchema,
    response: planning.ArchivePlanResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  plansDelete: route({
    operationId: 'plansDelete',
    method: 'DELETE',
    path: '/api/plans/:planId',
    context: 'planning',
    summary: 'Delete a plan and its whole breakdown',
    request: planning.DeletePlanRequestSchema,
    response: planning.DeletePlanResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  plansAddTask: route({
    operationId: 'plansAddTask',
    method: 'POST',
    path: '/api/plans/:planId/tasks',
    context: 'planning',
    summary: 'Add a task the method did not ask for',
    request: planning.AddPlanTaskRequestSchema,
    response: planning.PlanCommandResponseSchema,
    roles: PLANNERS,
    successStatus: 201,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'VALIDATION_FAILED'],
  }),
  plansUpdateTask: route({
    operationId: 'plansUpdateTask',
    method: 'PATCH',
    path: '/api/plans/:planId/tasks/:taskId',
    context: 'planning',
    summary: 'Curate one task: wording, owner, duration, position',
    request: planning.UpdatePlanTaskRequestSchema,
    response: planning.PlanCommandResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'VALIDATION_FAILED'],
  }),
  plansDeleteTask: route({
    operationId: 'plansDeleteTask',
    method: 'DELETE',
    path: '/api/plans/:planId/tasks/:taskId',
    context: 'planning',
    summary: 'Delete a task and everything beneath it',
    request: planning.DeletePlanTaskRequestSchema,
    response: planning.DeletePlanTaskResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),
  plansSetDependency: route({
    operationId: 'plansSetDependency',
    method: 'PUT',
    path: '/api/plans/:planId/dependencies',
    context: 'planning',
    summary: 'Declare or replace one dependency between two tasks',
    request: planning.SetPlanDependencyRequestSchema,
    response: planning.SetPlanDependencyResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT', 'VALIDATION_FAILED'],
  }),
  plansDeleteDependency: route({
    operationId: 'plansDeleteDependency',
    method: 'DELETE',
    path: '/api/plans/:planId/dependencies/:dependencyId',
    context: 'planning',
    summary: 'Drop one dependency',
    request: planning.DeletePlanDependencyRequestSchema,
    response: planning.DeletePlanDependencyResponseSchema,
    roles: PLANNERS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE', 'REVISION_CONFLICT'],
  }),

  /* ── attachments ──────────────────────────────────────────────────────── */
  attachmentsPresignUpload: route({
    operationId: 'attachmentsPresignUpload',
    method: 'POST',
    path: '/api/attachments/presign',
    context: 'attachments',
    summary: 'Presign a direct-to-storage upload',
    request: attachments.PresignUploadRequestSchema,
    response: attachments.PresignUploadResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 201,
    errors: ['PAYLOAD_TOO_LARGE', 'UNSUPPORTED_MEDIA_TYPE', 'NOT_FOUND'],
  }),
  attachmentsConfirmUpload: route({
    operationId: 'attachmentsConfirmUpload',
    method: 'POST',
    path: '/api/attachments/:attachmentId/confirm',
    context: 'attachments',
    summary: 'Confirm an upload landed in storage',
    request: attachments.ConfirmUploadRequestSchema,
    response: attachments.ConfirmUploadResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'CONFLICT'],
  }),
  attachmentsList: route({
    operationId: 'attachmentsList',
    method: 'GET',
    path: '/api/attachments',
    context: 'attachments',
    summary: 'List attachments of an owner',
    request: attachments.ListAttachmentsRequestSchema,
    response: attachments.ListAttachmentsResponseSchema,
    roles: ANY_ROLE,
    successStatus: 200,
  }),
  attachmentsDelete: route({
    operationId: 'attachmentsDelete',
    method: 'DELETE',
    path: '/api/attachments/:attachmentId',
    context: 'attachments',
    summary: 'Delete an attachment and its stored object',
    request: attachments.DeleteAttachmentRequestSchema,
    response: attachments.DeleteAttachmentResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),
  attachmentsAddLink: route({
    operationId: 'attachmentsAddLink',
    method: 'POST',
    path: '/api/attachments/links',
    context: 'attachments',
    summary: 'Attach an external link (stored as data; never fetched)',
    request: attachments.AddExternalLinkRequestSchema,
    response: attachments.AddExternalLinkResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 201,
    errors: ['NOT_FOUND'],
  }),
  attachmentsRemoveLink: route({
    operationId: 'attachmentsRemoveLink',
    method: 'DELETE',
    path: '/api/attachments/links/:linkId',
    context: 'attachments',
    summary: 'Remove an external link',
    request: attachments.RemoveExternalLinkRequestSchema,
    response: attachments.RemoveExternalLinkResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
    errors: ['NOT_FOUND'],
  }),

  /* ── ai-assist ────────────────────────────────────────────────────────── */
  aiAssistElaborate: route({
    operationId: 'aiAssistElaborate',
    method: 'POST',
    path: '/api/ai-assist/elaborate',
    context: 'ai-assist',
    summary: 'Draft or elaborate text for one element',
    request: assist.ElaborateRequestSchema,
    response: assist.ElaborateResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
    errors: ['ASSIST_UNAVAILABLE', 'ASSIST_BUDGET_EXCEEDED', 'NOT_FOUND'],
  }),
  aiAssistElaborateStream: route({
    operationId: 'aiAssistElaborateStream',
    method: 'POST',
    path: '/api/ai-assist/elaborate/stream',
    context: 'ai-assist',
    summary: 'Stream elaboration as NDJSON chunks',
    request: assist.ElaborateStreamRequestSchema,
    response: assist.ElaborateStreamResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
    streaming: true,
    errors: ['ASSIST_UNAVAILABLE', 'ASSIST_BUDGET_EXCEEDED', 'NOT_FOUND'],
  }),
  aiAssistCancel: route({
    operationId: 'aiAssistCancel',
    method: 'POST',
    path: '/api/ai-assist/invocations/:invocationId/cancel',
    context: 'ai-assist',
    summary: 'Cancel a running invocation',
    request: assist.CancelInvocationRequestSchema,
    response: assist.CancelInvocationResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
    errors: ['NOT_FOUND', 'INVALID_STATE'],
  }),
  aiAssistListInvocations: route({
    operationId: 'aiAssistListInvocations',
    method: 'GET',
    path: '/api/ai-assist/invocations',
    context: 'ai-assist',
    summary: 'List assist invocations with provenance',
    request: assist.ListInvocationsRequestSchema,
    response: assist.ListInvocationsResponseSchema,
    roles: CONTRIBUTORS,
    successStatus: 200,
  }),
} as const;

export type RouteManifest = typeof ROUTES;
export type RouteKey = keyof RouteManifest;
export type Route<TKey extends RouteKey = RouteKey> = RouteManifest[TKey];

export const ROUTE_KEYS = Object.keys(ROUTES) as readonly RouteKey[];
/**
 * Widened to `AnyRoute` on purpose: each manifest entry keeps its exact literal
 * type (the client's per-operation types depend on it), but a list of 86 distinct
 * object types is unusable for generic walks like the OpenAPI generator.
 */
export const ROUTE_LIST: readonly AnyRoute[] = ROUTE_KEYS.map((key) => ROUTES[key]);
